/**
 * Resolving the trailer load status shown on an order.
 *
 * `trailers.load_status` is the trailer's *live* physical state — it changes on
 * every check-in/check-out/audit. Reading it directly for a delivered order
 * makes historical trips drift: a trailer that ran empty last week shows up as
 * "loaded" once it is loaded for an unrelated trip.
 *
 * For delivered orders we therefore prefer the point-in-time value recorded in
 * `trailer_events` at or before the order's arrival (the same rule the Missing
 * BOL / Unbillable dashboard gating applies per leg in `billing-exclusion.ts`).
 *
 * NOTE: this is the *trailer's physical* load state, a different concept from
 * `legs.is_loaded` (the dispatcher's planned load state). See
 * docs/TRAILER_LOAD_STATUS.md.
 */

export type TrailerLoadStatus = "loaded" | "empty";

export interface HistoricalLoadStatus {
  load_status: string | null;
  occurred_at?: string | null;
  event_type?: string | null;
}

export interface ResolvedLoadStatus {
  /** Normalized value to display, or null when there is nothing to show. */
  value: TrailerLoadStatus | null;
  /** True when `value` came from the trailer's history at delivery time. */
  isHistorical: boolean;
  /** When the source event occurred (historical only). */
  occurredAt: string | null;
  /** Source event type, e.g. `checked_in` / `audited` (historical only). */
  eventType: string | null;
}

const EMPTY: ResolvedLoadStatus = {
  value: null,
  isHistorical: false,
  occurredAt: null,
  eventType: null,
};

/** Legacy rows may carry `unknown`; that is never displayed. */
export function normalizeLoadStatus(
  value: string | null | undefined,
): TrailerLoadStatus | null {
  if (value === "loaded" || value === "empty") return value;
  return null;
}

export function resolveOrderLoadStatus(input: {
  /** Order status; only `delivered` uses the historical value. */
  status?: string | null;
  /** Current `trailers.load_status` for the row's trailer. */
  liveLoadStatus?: string | null;
  /** Row from `order_load_status_at_delivery`, when available. */
  historical?: HistoricalLoadStatus | null;
}): ResolvedLoadStatus {
  const { status, liveLoadStatus, historical } = input;

  if (status === "delivered") {
    const hist = normalizeLoadStatus(historical?.load_status);
    if (hist) {
      return {
        value: hist,
        isHistorical: true,
        occurredAt: historical?.occurred_at ?? null,
        eventType: historical?.event_type ?? null,
      };
    }
  }

  const live = normalizeLoadStatus(liveLoadStatus);
  return live ? { ...EMPTY, value: live } : EMPTY;
}

const EVENT_LABELS: Record<string, string> = {
  checked_in: "check-in",
  checked_out: "check-out",
  audited: "yard audit",
  driver_verified: "driver verification",
};

/** Tooltip copy explaining where a resolved value came from. */
export function describeLoadStatusSource(
  resolved: ResolvedLoadStatus,
  formatDate: (iso: string) => string,
): string | null {
  if (!resolved.value) return null;
  if (!resolved.isHistorical) return "Trailer's current load status";
  const source = resolved.eventType ? EVENT_LABELS[resolved.eventType] ?? resolved.eventType : null;
  const when = resolved.occurredAt ? formatDate(resolved.occurredAt) : null;
  const parts = ["Load status at delivery"];
  if (source) parts.push(`recorded at ${source}`);
  if (when) parts.push(when);
  return parts.join(" — ");
}
